import { FC, FocusEventHandler, PropsWithChildren, Ref, useState } from 'react'

import { PickupDeliveryLocationResponseV1 } from '@forto-dev/customer-master-data-models'

import {
  LocationSelectorContext,
  LocationSelectorContextState,
  LocationSelectProps,
} from './useLocationSelectorContext'
import {
  LocationSelectorDispatchContext,
  LocationSelectorDispatchContextState,
} from './useLocationSelectorDispatchContext'

export type LocationSelectorContextProviderProps = PropsWithChildren<{
  accountID: string
  value?: string
  label?: string
  required?: boolean
  inputRef?: Ref<unknown>
  errorMessage?: string
  disabled?: boolean
  onChange?: (selectedAddress: PickupDeliveryLocationResponseV1 | null) => void
  onBlur?: FocusEventHandler<HTMLDivElement>
  manualActionProps?: {
    buttonLabel?: string
    dialogTitle?: string
    dialogDescription?: string
  }
  selectProps?: LocationSelectProps
}>

export const LocationSelectorContextProvider: FC<LocationSelectorContextProviderProps> = (props) => {
  const {
    children,
    accountID,
    value: valueFromProp,
    label,
    required,
    inputRef,
    errorMessage,
    disabled,
    onChange,
    onBlur,
    manualActionProps,
    selectProps,
  } = props
  const [manualMode, setManualMode] = useState(false)
  const [newLocationID, setNewLocationID] = useState<string | null>(null)

  const value = newLocationID ?? valueFromProp

  const onAddNew = () => {
    setManualMode(true)
  }

  const onSelect = (selectedLocation: PickupDeliveryLocationResponseV1 | null) => {
    setNewLocationID(null)
    onChange?.(selectedLocation)
  }

  const onAddNewCompleted = (newLocation?: PickupDeliveryLocationResponseV1) => {
    if (newLocation) {
      setNewLocationID(newLocation.id)
      onChange?.(newLocation)
    }
    setManualMode(false)
  }

  const state: LocationSelectorContextState = {
    accountID,
    manualMode,
    value,
    label,
    required,
    inputRef,
    errorMessage,
    disabled,
    manualActionProps,
    selectProps,
  }

  const dispatchState: LocationSelectorDispatchContextState = {
    onSelect,
    onAddNew,
    onAddNewCompleted,
    onBlur,
  }

  return (
    <LocationSelectorContext.Provider value={state}>
      <LocationSelectorDispatchContext.Provider value={dispatchState}>
        {children}
      </LocationSelectorDispatchContext.Provider>
    </LocationSelectorContext.Provider>
  )
}
